"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import {
  declineMeeting,
  acceptMeeting,
  counterMeeting,
  showAcceptedConfirmation,
  setMeeting,
  saveRateAdjustmentDraft,
  clearRateAdjustmentDraft,
} from "@/store/apps/meetings/MeetingDataSlice";
import { useRouter } from "next/navigation";

const checkSufficientAvailability = (availData, requiredDuration) => {
  if (!availData?.times || !requiredDuration) return false;

  const requiredMinutes = parseInt(requiredDuration, 10);

  const hasAnyTimeSlots = Object.values(availData.times).some(
    (daySlots) => Array.isArray(daySlots) && daySlots.length > 0,
  );

  if (!hasAnyTimeSlots) return false;

  return Object.values(availData.times).some((daySlots) => {
    if (!Array.isArray(daySlots) || daySlots.length === 0) return false;

    return daySlots.some((slot) => {
      if (
        !slot ||
        !slot.start ||
        !slot.end ||
        !slot.start._seconds ||
        !slot.end._seconds
      ) {
        return false;
      }

      const startTime = slot.start._seconds;
      const endTime = slot.end._seconds;
      const durationSeconds = endTime - startTime;
      const durationMinutes = durationSeconds / 60;

      return durationMinutes >= requiredMinutes;
    });
  });
};

const ReceiveModal = ({
  open,
  handleClose,
  content,
  fromAvailabilityPage = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const router = useRouter();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [seekerName, setSeekerName] = useState("");
  const [showRateAdjust, setShowRateAdjust] = useState(false);
  const [rateInputStr, setRateInputStr] = useState("");
  const [adjustedRate, setAdjustedRate] = useState(null);
  const [showGoalDialog, setShowGoalDialog] = useState(false);

  const currentMeetings = useSelector((state) => state.meetings.data);
  const availData = useSelector((state) => state.availability.data);

  const availability =
    availData?.available &&
    availData?.times &&
    Object.values(availData.times).some(
      (daySlots) => Array.isArray(daySlots) && daySlots.length > 0,
    );

  const duration = content?.session?.duration;
  const rate = content?.session?.session_rate;
  const hasSufficientAvailability =
    Boolean(availability) && checkSufficientAvailability(availData, duration);
  const goal = (
    content?.session?.goal_enhanced ||
    content?.session?.goal_raw ||
    ""
  )
    .replace(/^"|"$/g, "")
    .replace(/\b(Dr|Prof|Mr|Mrs|Ms)\.?\s+/gi, "")
    .trim();
  const displayedOffer = adjustedRate ?? rate;
  const hasInvalidRate =
    rateInputStr !== "" &&
    adjustedRate === null &&
    rateInputStr !== String(rate);

  const requestId = content?.request_id || content?.requestId;
  const sharerId = content?.sharer_id || content?.sharerId;
  const rateDraft = useSelector(
    (state) => state.meetings.rateAdjustmentDrafts?.[requestId],
  );
  const name = (content?.seeker?.given_name || "").replace(
    /\b(Dr|Prof|Mr|Mrs|Ms)\.?\s+/gi,
    "",
  );
  const lastName = (content?.seeker?.family_name || "").replace(
    /\b(Dr|Prof|Mr|Mrs|Ms)\.?\s+/gi,
    "",
  );
  const fullName = [name, lastName].filter(Boolean).join(" ") || "Someone";

  const expiry = 7;

  useEffect(() => {
    if (content?.seeker?.given_name) {
      setSeekerName(content.seeker.given_name);
    }
  }, [content]);

  useEffect(() => {
    if (open) {
      setIsSubmitted(false);
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    } else {
      setShowRateAdjust(false);
      setRateInputStr("");
      setAdjustedRate(null);
      setShowGoalDialog(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const restoredRate =
      rate > 0 && rateDraft?.originalRate === rate
        ? rateDraft.adjustedRate
        : null;
    setShowRateAdjust(restoredRate !== null);
    setRateInputStr(
      restoredRate !== null
        ? String(restoredRate)
        : rate > 0
        ? String(rate)
        : "",
    );
    setAdjustedRate(restoredRate);
  }, [open, requestId, rate, rateDraft]);

  const closeRequest = () => {
    dispatch(clearRateAdjustmentDraft(requestId));
    handleClose();
  };

  const GOAL_PREVIEW_WORDS = 60;
  const goalWords = goal ? goal.split(/\s+/).filter(Boolean) : [];
  const isLongGoal = goalWords.length > GOAL_PREVIEW_WORDS;
  const shouldTruncateGoal = isLongGoal;
  const goalPreview = isLongGoal
    ? `${goalWords.slice(0, GOAL_PREVIEW_WORDS).join(" ")}...`
    : goal;

  const handleAccept = async () => {
    if (isSubmitted) return;
    setIsSubmitted(true);
    setSeekerName(name);

    const currentMeeting = currentMeetings.find((m) => m.id === requestId);

    if (currentMeeting) {
      const now = new Date().toISOString();

      dispatch(
        setMeeting({
          ...currentMeeting,
          status: "accepted",
          status_time: now,
          status_log: [
            ...(currentMeeting.status_log || []),
            {
              status: "accepted",
              time: now,
            },
          ],
          session: {
            ...currentMeeting.session,
            ...(adjustedRate !== null &&
              adjustedRate !== rate && {
                session_rate: adjustedRate,
                original_session_rate: rate,
                rate_adjusted_by_sharer: true,
              }),
          },
        }),
      );
    }

    await dispatch(
      acceptMeeting({
        requestId,
        sharerId,
        ...(adjustedRate !== null && adjustedRate !== rate && { adjustedRate }),
      }),
    ).unwrap();

    closeRequest();
    dispatch(showAcceptedConfirmation(seekerName || name));

    if (fromAvailabilityPage) {
      router.push("/");
    }
  };

  const handleCounter = async () => {
    if (isSubmitted) return;
    setIsSubmitted(true);

    const currentMeeting = currentMeetings.find((m) => m.id === requestId);

    if (currentMeeting) {
      const now = new Date().toISOString();
      dispatch(
        setMeeting({
          ...currentMeeting,
          status: "countered",
          status_time: now,
          status_log: [
            ...(currentMeeting.status_log || []),
            { status: "countered", time: now },
          ],
          session: {
            ...currentMeeting.session,
            proposed_rate: adjustedRate,
            original_session_rate: rate,
            rate_countered_by_sharer: true,
          },
        }),
      );
    }

    await dispatch(
      counterMeeting({ requestId, sharerId, proposedRate: adjustedRate }),
    ).unwrap();

    closeRequest();
    dispatch(showAcceptedConfirmation(seekerName || name));

    if (fromAvailabilityPage) {
      router.push("/");
    }
  };

  const handleDecline = async () => {
    if (isSubmitted) return;
    setIsSubmitted(true);

    const currentMeeting = currentMeetings.find((m) => m.id === requestId);

    if (currentMeeting) {
      const now = new Date().toISOString();

      dispatch(
        setMeeting({
          ...currentMeeting,
          status: "declined",
          status_time: now,
          status_log: [
            ...(currentMeeting.status_log || []),
            {
              status: "declined",
              time: now,
            },
          ],
        }),
      );
    }

    await dispatch(declineMeeting({ requestId, sharerId })).unwrap();
    closeRequest();
  };

  const handleSetupAvailability = () => {
    if (requestId && adjustedRate !== null) {
      dispatch(
        saveRateAdjustmentDraft({
          requestId,
          originalRate: rate,
          adjustedRate,
        }),
      );
    } else {
      dispatch(clearRateAdjustmentDraft(requestId));
    }
    handleClose();
    if (!fromAvailabilityPage) {
      router.push("/availability");
    }
  };

  const handleRateInput = (e) => {
    const str = e.target.value;
    const n = parseFloat(str);

    if (!Number.isNaN(n)) {
      // Clamp: max $50,000, min $15 (0 is allowed and means waiving the fee).
      let clamped = Math.round(n);
      if (clamped > 50000) clamped = 50000;
      else if (clamped > 0 && clamped < 15) clamped = 15;

      setRateInputStr(String(clamped));
      setAdjustedRate(clamped);
    } else {
      setRateInputStr(str);
      setAdjustedRate(null);
    }
  };

  const createBodyContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          p: 2.5,
          borderRadius: "12px",
          backgroundColor: "action.hover",
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            Duration
          </Typography>
          <Typography
            sx={{
              fontSize: "1.125rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {duration} minutes
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            Offer for your time
          </Typography>
          <Typography
            aria-live="polite"
            sx={{
              fontSize: "1.125rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {displayedOffer > 0 ? `$${displayedOffer} USD` : "Free meeting"}
          </Typography>
        </Box>
      </Box>

      {goal && (
        <Box>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            About this meeting
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: "text.primary",
              lineHeight: 1.65,
              fontSize: "0.9375rem",
              overflowWrap: "anywhere",
            }}
          >
            {shouldTruncateGoal ? goalPreview : goal}
          </Typography>
          {shouldTruncateGoal && (
            <Box
              component="button"
              type="button"
              onClick={() => setShowGoalDialog(true)}
              sx={{
                mt: 1,
                p: 0,
                border: 0,
                background: "transparent",
                color: "text.primary",
                font: "inherit",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { color: "text.secondary" },
              }}
            >
              Read full goal
            </Box>
          )}
        </Box>
      )}

      {rate > 0 && (
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5 }}>
          <Box
            component="button"
            type="button"
            aria-expanded={showRateAdjust}
            aria-controls="receive-offer-editor"
            onClick={() => setShowRateAdjust((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              p: 0,
              border: 0,
              background: "transparent",
              color: "text.primary",
              font: "inherit",
              cursor: "pointer",
              textAlign: "left",
              "&:hover": { color: "text.secondary" },
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
            >
              Adjust your offer
            </Typography>
            {showRateAdjust ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            )}
          </Box>
          <Collapse in={showRateAdjust} id="receive-offer-editor">
            <Box sx={{ pt: 1.5 }}>
              <TextField
                size="small"
                type="number"
                value={rateInputStr}
                onChange={handleRateInput}
                error={hasInvalidRate}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      max: 50000,
                      step: 1,
                      "aria-label": "Offer amount",
                      "aria-describedby": "receive-offer-help",
                    },
                  },
                }}
                sx={{
                  width: 160,
                  "& .MuiInputBase-root": { height: 44, borderRadius: "8px" },
                }}
              />
              <Typography
                id="receive-offer-help"
                variant="body2"
                role="status"
                sx={{
                  mt: 1,
                  minHeight: 21,
                  color: hasInvalidRate ? "error.main" : "text.secondary",
                  lineHeight: 1.5,
                }}
              >
                {hasInvalidRate
                  ? "Enter a valid amount."
                  : adjustedRate !== null && adjustedRate > rate
                  ? `The seeker must accept your $${adjustedRate} counter-offer.`
                  : adjustedRate === 0
                  ? "This meeting will be free."
                  : adjustedRate !== null && adjustedRate < rate
                  ? `Offer lowered to $${adjustedRate} (from $${rate}).`
                  : "Set the amount to $0 for a free meeting."}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}

      {!hasSufficientAvailability ? (
        <Box>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: "text.primary",
              mb: 0.75,
            }}
          >
            {availability
              ? "Add a longer time slot"
              : "Add availability to continue"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            {availability
              ? `Add a time slot of at least ${duration} minutes before accepting this request.`
              : `Add times for a ${duration}-minute meeting so ${name ||
                  "the requester"} can choose a time after you accept.`}
            {rate > 0
              ? " Your offer will be saved while you add availability."
              : ""}
          </Typography>
        </Box>
      ) : (
        <Box
          component="button"
          type="button"
          onClick={handleSetupAvailability}
          sx={{
            alignSelf: "flex-start",
            p: 0,
            border: 0,
            background: "transparent",
            color: "text.secondary",
            font: "inherit",
            fontSize: "0.875rem",
            cursor: "pointer",
            "&:hover": { color: "text.primary" },
          }}
        >
          Review availability
        </Box>
      )}
    </Box>
  );

  const getActionConfig = () => {
    if (!availability) {
      return {
        confirmButtonText: "Set availability",
        onConfirm: handleSetupAvailability,
        showIgnoreButton: true,
        ignoreButtonText: "Decline",
        onIgnore: handleDecline,
      };
    }

    if (!hasSufficientAvailability) {
      return {
        confirmButtonText: "Update availability",
        onConfirm: handleSetupAvailability,
        showIgnoreButton: true,
        ignoreButtonText: "Decline",
        onIgnore: handleDecline,
      };
    }

    const isCounterOffer = adjustedRate !== null && adjustedRate > rate;
    const hasDecrease =
      adjustedRate !== null && adjustedRate !== rate && adjustedRate <= rate;

    return {
      confirmButtonText: isCounterOffer
        ? `Counter-offer ($${adjustedRate})`
        : hasDecrease
        ? adjustedRate === 0
          ? "Accept (No fee)"
          : `Accept ($${adjustedRate})`
        : "Accept",
      onConfirm: isCounterOffer ? handleCounter : handleAccept,
      showIgnoreButton: true,
      ignoreButtonText: "Decline",
      onIgnore: handleDecline,
    };
  };

  const actionConfig = getActionConfig();

  return (
    <>
      <ConfirmDialog
        open={open}
        onClose={closeRequest}
        title="Meeting request"
        subtitle={`From ${fullName} · Respond within ${expiry} days`}
        body={createBodyContent()}
        onConfirm={actionConfig.onConfirm}
        onIgnore={actionConfig.onIgnore}
        confirmButtonText={actionConfig.confirmButtonText}
        ignoreButtonText={actionConfig.ignoreButtonText}
        showConfirmButton={true}
        showIgnoreButton={actionConfig.showIgnoreButton}
        showCloseIcon={true}
        isProcessing={isSubmitted}
        processingText="Processing..."
        modalStyle="center"
        backdropStyle="frosted"
        minHeight="auto"
        customStyle={{
          width: "min(620px, calc(100vw - 48px))",
          px: 0,
          borderRadius: "16px",
          maxHeight: "calc(100dvh - 48px)",
          overflowY: "auto",
        }}
        contentSx={{ px: 4, pt: 4, pb: 2.5 }}
        titleSx={{
          fontSize: "1.625rem",
          lineHeight: 1.25,
          letterSpacing: "-0.5px",
          mb: 0.75,
        }}
        subtitleSx={{ fontSize: "0.875rem", lineHeight: 1.5, mb: 3 }}
        bodySx={{ mb: 3 }}
        mobileContentSx={{ borderRadius: "16px 16px 0 0" }}
        confirmButtonVariant="text"
        ignoreButtonSx={{ color: "text.secondary" }}
        disableConfirm={hasSufficientAvailability && hasInvalidRate}
        disableBackdropClick={false}
        disableEscapeKeyDown={false}
      />

      <Dialog
        open={showGoalDialog}
        onClose={() => setShowGoalDialog(false)}
        aria-labelledby="meeting-goal-title"
        maxWidth={false}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(0,0,0,0.4)",
            },
          },
          paper: {
            sx: {
              width: "min(620px, calc(100vw - 32px))",
              maxWidth: "calc(100vw - 32px)",
              m: 2,
              borderRadius: "16px",
              maxHeight: "calc(100dvh - 32px)",
            },
          },
        }}
      >
        <DialogTitle
          id="meeting-goal-title"
          sx={{
            p: { xs: 3, sm: 4 },
            pb: { xs: 2, sm: 2 },
            pr: 7,
            fontSize: "1.625rem",
            fontWeight: 600,
            lineHeight: 1.25,
            letterSpacing: "-0.5px",
          }}
        >
          Meeting goal
          <IconButton
            aria-label="Close meeting goal"
            onClick={() => setShowGoalDialog(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 4 } }}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              lineHeight: 1.7,
              color: "text.primary",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {goal}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReceiveModal;
